import { useQuery } from "@tanstack/react-query";
import { RotateCcw } from "lucide-react";
import { useState } from "react";
import { api } from "../../shared/api/client";
import { Button, Card, StatusBadge } from "../../shared/ui";
import type { CarryoverView } from "../../shared/types/api";

/**
 * Work in the backlog because a sprint did not finish it.
 *
 * <p>Sits above the backlog rather than being folded into it. These items are in the list too,
 * each with its own badge, but a planning session needs the set in one place: an item carried
 * twice for the same reason is a different conversation from one nobody has picked up yet, and
 * that pattern is invisible when the rows are scattered through two hundred others.
 *
 * <p>Collapsed to a summary line when there are more than three, because the point is to be
 * noticed rather than to take over the page.
 */
export function CarryoverBanner({
  projectId,
  onSelect,
}: {
  projectId: string;
  onSelect: (workItemIds: string[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const waiting = useQuery({
    queryKey: ["backlog-carryover", projectId],
    queryFn: () =>
      api.get<CarryoverView[]>(`/api/v1/backlog/carryover?projectId=${projectId}`),
    enabled: Boolean(projectId),
  });

  const rows = waiting.data ?? [];
  if (!waiting.isSuccess || rows.length === 0) {
    return null;
  }

  // Named in the heading when it is all from one sprint, which is the ordinary case.
  const sprintNames = [...new Set(rows.map((row) => row.fromSprintName))];
  const heading =
    sprintNames.length === 1
      ? `${rows.length} ${rows.length === 1 ? "item is" : "items are"} undone from ${sprintNames[0]}`
      : `${rows.length} items are undone from ${sprintNames.length} earlier sprints`;

  const shown = expanded ? rows : rows.slice(0, 3);

  return (
    <Card className="mb-4 border-amber-200 bg-amber-50/50">
      <div className="p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <RotateCcw className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-ink-900">{heading}</p>
              <p className="mt-0.5 text-xs text-ink-600">
                Waiting here until somebody plans them in. The next sprint you create picks them
                up automatically.
              </p>
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => onSelect(rows.map((row) => row.workItemId))}
          >
            Select all {rows.length}
          </Button>
        </div>

        <ul className="mt-3 space-y-1.5">
          {shown.map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-mono text-ink-500">{row.itemKey}</span>
              <span className="truncate text-ink-800">{row.title}</span>
              <StatusBadge tone="amber">{row.reasonLabel}</StatusBadge>
              {row.comment && <span className="text-ink-500">{row.comment}</span>}
              <span className="text-ink-400">· {row.fromSprintName}</span>
            </li>
          ))}
        </ul>

        {rows.length > 3 && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 -ml-2"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? "Show fewer" : `Show all ${rows.length}`}
          </Button>
        )}
      </div>
    </Card>
  );
}
